import React, { useEffect, useState } from "react";
import NavBar from "@/components/NavBar";
import api from "@/lib/api";
import { fmt } from "@/lib/format";
import { toast } from "sonner";

function groupByDay(rides) {
  const g = {};
  rides.forEach(r => {
    const d = new Date(r.scheduled_for);
    const key = d.toISOString().slice(0, 10);
    if (!g[key]) g[key] = [];
    g[key].push(r);
  });
  return g;
}

function formatDay(iso) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  const long = d.toLocaleDateString("fr-CH", { weekday: "long", day: "2-digit", month: "long" });
  if (diff === 0) return `AUJOURD'HUI · ${long}`;
  if (diff === 1) return `DEMAIN · ${long}`;
  return long.toUpperCase();
}

function canRelease(r) {
  if (r.status !== "accepted" || !r.scheduled_for) return false;
  return new Date(r.scheduled_for).getTime() - Date.now() >= 24 * 3600 * 1000;
}

export default function DriverAgenda() {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      const { data } = await api.get("/driver/agenda");
      setRides(data || []);
    } catch { toast.error("Échec du chargement de l'agenda"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const release = async (rideId) => {
    if (!window.confirm("Annuler cette course planifiée ? (préavis 24h requis)")) return;
    try {
      await api.post(`/rides/${rideId}/driver-release`);
      toast.success("Course remise en file");
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Annulation impossible"); }
  };

  const grouped = groupByDay(rides);
  const dayKeys = Object.keys(grouped).sort();

  return (
    <div className="min-h-screen bg-white">
      <NavBar><span className="font-mono-eyebrow hidden md:inline">// Agenda</span></NavBar>
      <main className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="driver-agenda-page">
        <p className="font-mono-eyebrow">// Mes courses planifiées</p>
        <h1 className="font-display text-4xl mt-1">AGENDA</h1>

        {loading ? (
          <p className="mt-6 font-mono-eyebrow">// Chargement...</p>
        ) : dayKeys.length === 0 ? (
          <div className="mt-6 brut-card p-6" data-testid="agenda-empty">
            <p className="font-display text-2xl">AUCUNE COURSE PLANIFIÉE</p>
            <p className="mt-2 text-[#52525B]">Acceptez des courses planifiées depuis le tableau de bord, elles apparaîtront ici.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
            {dayKeys.map(day => (
              <section key={day} data-testid={`agenda-day-${day}`}>
                <div className="border-b-2 border-black pb-2 mb-3 flex justify-between items-end">
                  <h2 className="font-display text-2xl">{formatDay(day)}</h2>
                  <span className="font-mono-eyebrow">{grouped[day].length} COURSE{grouped[day].length > 1 ? "S" : ""}</span>
                </div>
                <div className="space-y-3">
                  {grouped[day].map(r => (
                    <div key={r.ride_id} className="brut-card p-4 grid sm:grid-cols-12 gap-4 items-center" data-testid={`agenda-ride-${r.ride_id}`}>
                      <div className="sm:col-span-2 text-center bg-[#FFD600] border-2 border-black p-3">
                        <p className="font-display text-3xl leading-none">{new Date(r.scheduled_for).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}</p>
                        <p className="font-mono-eyebrow mt-1">{r.status === "accepted" ? "CONFIRMÉE" : "À ACCEPTER"}</p>
                      </div>
                      <div className="sm:col-span-6">
                        <p className="font-display text-lg">{r.passenger_name}</p>
                        <p className="text-xs mt-1 text-[#52525B]">De : {r.pickup?.address?.slice(0, 70)}</p>
                        <p className="text-xs text-[#52525B]">À : {r.destination?.address?.slice(0, 70)}</p>
                      </div>
                      <div className="sm:col-span-2 text-right">
                        <p className="font-display text-2xl">{fmt(r.fare)}</p>
                        <p className="font-mono-eyebrow text-[#52525B]">{r.distance_km}KM · {r.vehicle_type?.toUpperCase()}</p>
                      </div>
                      <div className="sm:col-span-2 flex justify-end">
                        {canRelease(r) && (
                          <button
                            data-testid={`agenda-release-${r.ride_id}`}
                            onClick={() => release(r.ride_id)}
                            className="brut-btn brut-btn-secondary text-xs px-3 py-2"
                            style={{background:"#FF3B30",color:"white"}}
                          >Annuler</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
